import {
    GENERATION_SERIES,
    OBJECTIVE_OTHER_SERIES,
    type ObjectiveDefinition,
    type SpendSeries,
} from '@apps/web/components/admin/llm_spend/constants';

// Null when the denominator is zero: there is no rate against nothing, and a
// chart line should break rather than drop to a misleading zero.
export function ratio(numerator: number, denominator: number): number | null {
    return denominator === 0 ? null : numerator / denominator;
}

// Percent change of `current` against `previous`; null when either side is
// missing or the base is zero.
export function percentChange(
    current: number | null,
    previous: number | null,
): number | null {
    if (current === null || previous === null || previous === 0) {
        return null;
    }
    return ((current - previous) / previous) * 100;
}

type GenerationRecord<T> = Readonly<Record<string, T>>;

// One line per agent generation: `select` reads a numerator and denominator
// from that generation's metrics, and a missing generation leaves a gap.
export function generationRatioPoints<T>(
    buckets: ReadonlyArray<{
        readonly bucket_start: string;
        readonly by_generation: GenerationRecord<T>;
    }>,
    select: (metrics: T) => readonly [number, number],
): Array<{
    readonly bucketStart: string;
    readonly values: Record<string, number | null>;
}> {
    return buckets.map((row) => ({
        bucketStart: row.bucket_start,
        values: Object.fromEntries(
            GENERATION_SERIES.map((series) => {
                const metrics = row.by_generation[series.key];
                if (metrics === undefined) {
                    return [series.key, null];
                }
                const [numerator, denominator] = select(metrics);
                return [series.key, ratio(numerator, denominator)];
            }),
        ),
    }));
}

// Total spend per model from the per-model component breakdown.
export function spendByModel(
    spendByModelComponent: Readonly<
        Record<string, Readonly<Record<string, number>>>
    >,
): Record<string, number> {
    return Object.fromEntries(
        Object.entries(spendByModelComponent).map(([model, components]) => [
            model,
            Object.values(components).reduce((sum, value) => sum + value, 0),
        ]),
    );
}

type ObjectiveTotals = {
    readonly totalSpend: number;
    readonly wakeCount: number;
};

function addObjectiveValues(
    totalsByObjective: Map<string, ObjectiveTotals>,
    values: Readonly<Record<string, number>>,
    knownKeys: ReadonlySet<string>,
    metric: keyof ObjectiveTotals,
): void {
    for (const [key, value] of Object.entries(values)) {
        const objectiveKey = knownKeys.has(key)
            ? key
            : OBJECTIVE_OTHER_SERIES.key;
        const totals = totalsByObjective.get(objectiveKey) ?? {
            totalSpend: 0,
            wakeCount: 0,
        };
        totalsByObjective.set(objectiveKey, {
            ...totals,
            [metric]: totals[metric] + value,
        });
    }
}

export type ObjectiveBreakdownRow = ObjectiveDefinition & {
    readonly totalSpend: number;
    readonly wakeCount: number;
    readonly costPerWake: number | null;
    readonly costPerUnit: number | null;
    readonly share: number | null;
};

export function summarizeObjectiveBreakdown(
    buckets: ReadonlyArray<{
        readonly spend: Readonly<Record<string, number>>;
        readonly wakes: Readonly<Record<string, number>>;
    }>,
    knownSeries: ReadonlyArray<ObjectiveDefinition>,
    totalUnits: number,
): Array<ObjectiveBreakdownRow> {
    const knownKeys = new Set(knownSeries.map((objective) => objective.key));
    const totalsByObjective = new Map<string, ObjectiveTotals>();
    for (const bucket of buckets) {
        addObjectiveValues(
            totalsByObjective,
            bucket.spend,
            knownKeys,
            'totalSpend',
        );
        addObjectiveValues(
            totalsByObjective,
            bucket.wakes,
            knownKeys,
            'wakeCount',
        );
    }
    const definitions = [...knownSeries, OBJECTIVE_OTHER_SERIES];
    const totals = definitions.map((objective) => {
        const { totalSpend, wakeCount } = totalsByObjective.get(
            objective.key,
        ) ?? { totalSpend: 0, wakeCount: 0 };
        return {
            key: objective.key,
            label: objective.label,
            totalSpend,
            wakeCount,
            costPerWake: ratio(totalSpend, wakeCount),
            costPerUnit: ratio(totalSpend, totalUnits),
        };
    });
    const totalSpend = totals.reduce(
        (sum, objective) => sum + objective.totalSpend,
        0,
    );
    return totals
        .map((objective) => ({
            ...objective,
            share: ratio(objective.totalSpend, totalSpend),
        }))
        .filter(
            (objective) => objective.totalSpend > 0 || objective.wakeCount > 0,
        )
        .sort(
            (left, right) =>
                right.totalSpend - left.totalSpend ||
                left.label.localeCompare(right.label),
        );
}

// Rollup flow values are extensible (for example, agent.general), so select
// the seven largest keys for the current range and fold the rest into Other.
export function dimensionSeriesFor(
    records: ReadonlyArray<Readonly<Record<string, number>>>,
): Array<SpendSeries> {
    const totals: Record<string, number> = {};
    for (const record of records) {
        for (const [key, value] of Object.entries(record)) {
            totals[key] = (totals[key] ?? 0) + value;
        }
    }
    const keys = Object.entries(totals)
        .filter(([, value]) => value > 0)
        .sort(([, left], [, right]) => right - left)
        .map(([key]) => key);
    const visible = keys.slice(0, 7);
    const series = visible.map((key, index) => ({
        key,
        label: key.replace(/^agent\./, '').replace(/_/g, ' '),
        color: `var(--spend-series-${index + 1})`,
    }));
    return keys.length > visible.length
        ? [
              ...series,
              {
                  key: 'other',
                  label: 'Other',
                  color: 'var(--spend-series-other)',
              },
          ]
        : series;
}

export function foldDimensionSpend(
    values: Readonly<Record<string, number>>,
    series: ReadonlyArray<SpendSeries>,
): Record<string, number> {
    const visible = new Set(series.map((row) => row.key));
    const folded: Record<string, number> = {};
    for (const [key, value] of Object.entries(values)) {
        const target = visible.has(key) ? key : 'other';
        folded[target] = (folded[target] ?? 0) + value;
    }
    return folded;
}

const USD_WHOLE = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
});
const USD_CENTS = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
const USD_COMPACT = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
});

// Whole dollars above $100, cents below: "$21,361" and "$3.56" both read
// naturally, "$21,361.42" does not.
export function formatUsd(value: number | null): string {
    if (value === null) {
        return '—';
    }
    return Math.abs(value) >= 100
        ? USD_WHOLE.format(value)
        : USD_CENTS.format(value);
}

// Axis ticks: "$1.2K", "$20K".
export function formatUsdCompact(value: number): string {
    return Math.abs(value) >= 1000
        ? USD_COMPACT.format(value)
        : USD_WHOLE.format(value);
}

export function formatCount(value: number | null, digits = 1): string {
    return value === null ? '—' : value.toFixed(digits);
}
